import axios from 'axios';
import type { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiError } from './types';

// Base URL for API calls - should be configurable via environment variables
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

// Logout path - when this request returns 401 we don't call logout again (avoid loop)
const LOGOUT_PATH = '/merchant/v1/auth/logout/';

// Extended config for internal flags (avoid 401 handling on logout request)
export type ApiRequestConfig = AxiosRequestConfig & { _isLogoutRequest?: boolean };

// Create Axios instance with optimized configuration
export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: import.meta.env.PROD ? 8000 : 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
  maxRedirects: 3,
  // Treat 401 as error so the response interceptor runs; other 4xx are still "success" for caller handling
  validateStatus: (status) => status !== 401 && status < 500,
});

// Request interceptor for custom headers (no auth token needed with cookie authentication)
apiClient.interceptors.request.use(
  config => {
    // Add any custom headers here if needed in the future
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Helper function to detect current user type and clear appropriate auth data
const handleAuthError = () => {
  // Detect user type based on localStorage
  const isTeamUser = localStorage.getItem('isTeamUser') === 'true';
  const teamAuth = localStorage.getItem('team_auth');
  
  if (isTeamUser || teamAuth) {
    // Clear ONLY team user auth data
    localStorage.removeItem('team_auth');
    localStorage.removeItem('isTeamUser');
    localStorage.removeItem('teamUserData');
    localStorage.removeItem('team_user');
    localStorage.removeItem('team_token');
  } else {
    // Clear ONLY merchant auth data
    localStorage.removeItem('persist:root');
    localStorage.removeItem('persist:auth');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('auth_user');
  }
  
  // Set logout flag
  sessionStorage.setItem('user_logged_out', 'true');
  
  // Redirect based on current path
  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/superadmin')) {
    window.location.replace('/superadmin/auth/login');
  } else {
    window.location.replace('/auth/login');
  }
};

// Response interceptor for handling responses and errors
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as ApiRequestConfig;
    const status = error.response?.status;

    // Handle 401 (unauthorized): trigger logout API, clear auth, redirect to login
    if (status === 401 && originalRequest) {
      const isLogoutRequest =
        originalRequest._isLogoutRequest === true ||
        (typeof originalRequest.url === 'string' && originalRequest.url.includes('auth/logout'));

      if (!isLogoutRequest) {
        // Trigger logout API so server can invalidate session (best effort; ignore errors)
        try {
          await apiClient.post(LOGOUT_PATH, {}, { _isLogoutRequest: true } as ApiRequestConfig);
        } catch {
          // Logout API may also return 401 when cookies are gone; that's expected
        }
      }

      handleAuthError();
      return Promise.reject(error);
    }

    // Handle 403 (forbidden) - redirect to login directly
    if (status === 403) {
      handleAuthError();
      return Promise.reject(error);
    }

    // Handle other errors
    const apiError: ApiError = {
      success: false,
      message: error.response?.data?.message || error.message || 'An error occurred',
      errors: error.response?.data?.errors || [],
      status: error.response?.status,
    };

    return Promise.reject(apiError);
  }
);

// Generic API request method
export const apiRequest = async <T = unknown>(config: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await apiClient(config);
  return response.data;
};

// Convenience methods for different HTTP verbs
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'GET', url }),

  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'POST', url, data }),

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'PUT', url, data }),

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'PATCH', url, data }),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'DELETE', url }),
};
